/**
 * Channel Base - Base class and interfaces for channel adapters
 *
 * Channel adapters handle communication with specific messaging platforms
 * (Telegram, Discord, Slack, etc.)
 */

import type { ChannelScope, InboundMessage, OutboundMessage, SenderInfo } from "../types/index.js";

/** Channel adapter interface */
export interface ChannelAdapter {
	/** Channel type identifier */
	readonly channelType: string;

	/** Initialize the adapter */
	initialize(): Promise<void>;

	/** Shutdown the adapter */
	shutdown(): Promise<void>;

	/** Send a message to the channel */
	send(message: OutboundMessage): Promise<void>;

	/** Get channel profile/info */
	getProfile(): Promise<ChannelProfile>;

	/** Check if adapter is connected */
	isConnected(): boolean;

	/** Set message handler */
	onMessage(handler: (message: InboundMessage) => void): void;

	/** Set error handler */
	onError(handler: (error: Error) => void): void;
}

/** Channel monitor interface (for webhook-based channels) */
export interface ChannelMonitor {
	/** Start monitoring */
	start(): Promise<void>;

	/** Stop monitoring */
	stop(): Promise<void>;

	/** Get webhook URL (if applicable) */
	getWebhookUrl?(): string;

	/** Handle incoming webhook payload */
	handleWebhook?(payload: unknown): Promise<void>;
}

/** Channel configuration */
export interface ChannelConfig {
	/** Channel type */
	type: string;
	/** Channel name */
	name: string;
	/** Enabled */
	enabled: boolean;
	/** Channel-specific configuration */
	config: Record<string, unknown>;
	/** Access control */
	access: {
		dmPolicy: "pairing" | "allowlist" | "open";
		dmAllowlist?: string[];
		groupPolicy: "mention" | "allowlist" | "open";
		groupAllowlist?: string[];
	};
}

/** Channel profile */
export interface ChannelProfile {
	/** Channel type */
	type: string;
	/** Bot/app ID */
	id: string;
	/** Bot/app name */
	name: string;
	/** Username/handle */
	username?: string;
	/** Avatar URL */
	avatar?: string;
	/** Connected status */
	connected: boolean;
	/** Capabilities */
	capabilities: ChannelCapabilities;
}

/** Channel capabilities */
export interface ChannelCapabilities {
	/** Supports text messages */
	text: boolean;
	/** Supports images */
	images: boolean;
	/** Supports files */
	files: boolean;
	/** Supports reactions */
	reactions: boolean;
	/** Supports threads */
	threads: boolean;
	/** Supports editing messages */
	editMessages: boolean;
	/** Supports deleting messages */
	deleteMessages: boolean;
	/** Supports typing indicators */
	typing: boolean;
	/** Supports cards/buttons */
	cards: boolean;
	/** Supports voice/audio */
	voice: boolean;
	/** Maximum message length */
	maxMessageLength: number;
	/** Maximum file size */
	maxFileSize: number;
}

/** Channel scope types */
export type { ChannelScope };

/** Base channel adapter implementation */
export abstract class BaseChannelAdapter implements ChannelAdapter {
	abstract readonly channelType: string;

	protected config: ChannelConfig;
	protected connected = false;
	protected messageHandler?: (message: InboundMessage) => void;
	protected errorHandler?: (error: Error) => void;

	constructor(config: ChannelConfig) {
		this.config = config;
	}

	abstract initialize(): Promise<void>;
	abstract shutdown(): Promise<void>;
	abstract send(message: OutboundMessage): Promise<void>;
	abstract getProfile(): Promise<ChannelProfile>;

	isConnected(): boolean {
		return this.connected;
	}

	onMessage(handler: (message: InboundMessage) => void): void {
		this.messageHandler = handler;
	}

	onError(handler: (error: Error) => void): void {
		this.errorHandler = handler;
	}

	/**
	 * Build session key from channel and identifier
	 */
	protected buildSessionKey(agentId: string, scope: ChannelScope, identifier: string): string {
		return `agent:${agentId}:${this.channelType}:${scope}:${identifier}`;
	}

	/**
	 * Create inbound message
	 */
	protected createInboundMessage(params: {
		id: string;
		agentId: string;
		scope: ChannelScope;
		identifier: string;
		sender: SenderInfo;
		content:
			| { type: "text"; text: string }
			| { type: "image"; url: string }
			| { type: "file"; url: string; name: string }
			| { type: "command"; command: string; args: string[] };
		replyTo?: string;
		threadId?: string;
	}): InboundMessage {
		const sessionKey = this.buildSessionKey(params.agentId, params.scope, params.identifier);

		return {
			id: params.id,
			sessionKey,
			agentId: params.agentId,
			channel: this.channelType,
			sender: params.sender,
			content: params.content,
			timestamp: new Date(),
			replyTo: params.replyTo,
			threadId: params.threadId,
		};
	}

	/**
	 * Emit message to handler
	 */
	protected emitMessage(message: InboundMessage): void {
		if (this.messageHandler) {
			this.messageHandler(message);
		}
	}

	/**
	 * Emit error to handler
	 */
	protected emitError(error: Error): void {
		if (this.errorHandler) {
			this.errorHandler(error);
		}
	}

	/**
	 * Chunk text for platforms with message length limits
	 */
	protected chunkText(text: string, maxLength: number): string[] {
		const chunks: string[] = [];
		let remaining = text;

		while (remaining.length > 0) {
			if (remaining.length <= maxLength) {
				chunks.push(remaining);
				break;
			}

			// Find good break point
			let breakPoint = remaining.lastIndexOf("\n\n", maxLength);
			if (breakPoint < 0) breakPoint = remaining.lastIndexOf("\n", maxLength);
			if (breakPoint < 0) breakPoint = remaining.lastIndexOf(" ", maxLength);
			if (breakPoint < 0) breakPoint = maxLength;

			chunks.push(remaining.substring(0, breakPoint));
			remaining = remaining.substring(breakPoint).trim();
		}

		return chunks;
	}
}

/** Channel registry */
export class ChannelRegistry {
	private adapters = new Map<string, new (config: ChannelConfig) => ChannelAdapter>();

	/** Register a channel adapter */
	register(type: string, adapter: new (config: ChannelConfig) => ChannelAdapter): void {
		this.adapters.set(type, adapter);
	}

	/** Create adapter instance */
	create(type: string, config: ChannelConfig): ChannelAdapter {
		const Adapter = this.adapters.get(type);
		if (!Adapter) {
			throw new Error(`Unknown channel type: ${type}`);
		}
		return new Adapter(config);
	}

	/** Check if channel type is registered */
	has(type: string): boolean {
		return this.adapters.has(type);
	}

	/** Get all registered channel types */
	getTypes(): string[] {
		return Array.from(this.adapters.keys());
	}
}

/** Global channel registry instance */
export const globalChannelRegistry = new ChannelRegistry();
